'use client';
import React from 'react';
import { useTheme } from '@/lib/ThemeContext';

const TRUST_BADGES = [
  {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
    text: 'No upload required',
    color: 'text-blue-400',
    bg: 'rgba(59,130,246,0.14)',
    border: 'rgba(59,130,246,0.28)',
    bgLight: 'rgba(59,130,246,0.10)',
    borderLight: 'rgba(59,130,246,0.20)',
    colorLight: 'text-blue-500',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    text: 'Browser-based',
    color: 'text-cyan-400',
    bg: 'rgba(6,182,212,0.14)',
    border: 'rgba(6,182,212,0.28)',
    bgLight: 'rgba(6,182,212,0.10)',
    borderLight: 'rgba(6,182,212,0.20)',
    colorLight: 'text-cyan-500',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    text: 'Free tools',
    color: 'text-emerald-400',
    bg: 'rgba(16,185,129,0.14)',
    border: 'rgba(16,185,129,0.28)',
    bgLight: 'rgba(16,185,129,0.10)',
    borderLight: 'rgba(16,185,129,0.20)',
    colorLight: 'text-emerald-500',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18h3" />
      </svg>
    ),
    text: 'Mobile friendly',
    color: 'text-violet-400',
    bg: 'rgba(139,92,246,0.14)',
    border: 'rgba(139,92,246,0.28)',
    bgLight: 'rgba(139,92,246,0.10)',
    borderLight: 'rgba(139,92,246,0.20)',
    colorLight: 'text-violet-500',
  },
];

export default function HeroSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section className="relative pt-16 pb-12 px-4 sm:px-6 overflow-hidden">
      {/* Extra hero-specific blobs for depth */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute rounded-full"
          style={{
            width: 480,
            height: 480,
            top: '-120px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: isDark
              ? 'radial-gradient(circle, rgba(37,99,235,0.20) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 320,
            height: 320,
            top: '40px',
            right: '5%',
            background: isDark
              ? 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(139,92,246,0.10) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 280,
            height: 280,
            bottom: '-40px',
            left: '8%',
            background: isDark
              ? 'radial-gradient(circle, rgba(6,182,212,0.16) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(6,182,212,0.10) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
        />
      </div>
      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Eyebrow pill */}
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-7"
          style={{
            background: isDark ? 'rgba(30,41,59,0.80)' : 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: isDark ? '1px solid rgba(96,165,250,0.25)' : '1px solid rgba(37,99,235,0.18)',
            boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.30)' : '0 2px 12px rgba(37,99,235,0.08)',
          }}
        >
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          <span className={`text-xs font-semibold tracking-wide ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
            Browser-native · No sign-up required
          </span>
        </div>

        {/* Headline */}
        <h1
          className="section-title text-4xl sm:text-5xl md:text-6xl mb-5 leading-[1.12] tracking-tight"
          style={{ color: isDark ? '#f1f5f9' : '#0F172A' }}
        >
          Free Everyday Tools for{' '}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: isDark
                ? 'linear-gradient(135deg, #60a5fa 0%, #38bdf8 45%, #a78bfa 100%)'
                : 'linear-gradient(135deg, #2563EB 0%, #0ea5e9 45%, #7c3aed 100%)',
            }}
          >
            PDF, Images, Audio
          </span>{' '}
          &amp; More
        </h1>

        {/* Subtitle */}
        <p
          className="text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-medium"
          style={{ color: isDark ? '#94a3b8' : '#64748b' }}
        >
          Fast, private, and easy tools that work directly in your browser.
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
          <a
            href="#tools"
            className="btn-primary text-base px-8 py-3.5 w-full sm:w-auto"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Explore Tools
          </a>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          {TRUST_BADGES?.map((badge) => (
            <div
              key={badge?.text}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold ${isDark ? badge?.color : badge?.colorLight}`}
              style={{
                background: isDark ? badge?.bg : badge?.bgLight,
                border: `1px solid ${isDark ? badge?.border : badge?.borderLight}`,
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
            >
              {badge?.icon}
              {badge?.text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}